import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  ButtonGroup,
  Tooltip,
  NavItem,
  Nav,
  NavLink,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polygon,
  useMap,
} from "react-leaflet";
import classnames from "classnames";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { toast } from "react-toastify";
import { dashboard } from "../../helpers/backend_helper";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDivisions,
  fetchRanges,
  fetchRounds,
  fetchBeats,
  fetchRanges2,
  fetchRounds2,
} from "../../store/actions";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import L from "leaflet";
import "jspdf-autotable";
import { HeatmapLayer } from "react-leaflet-heatmap-layer-v3";
import { Download } from "lucide-react";
import CanvasOverlay from "./CanvasOverlay";
import IncidentAnalysis from "./IncidentAnalysis";
import { hideLoader, showLoader } from "../../helpers/loaderService";
import { DEATH_INJURED_CONFIG, GROUPING_OPTIONS, INCIDENT_CONFIG } from "./helper";
import { HeatmapLegend } from "./HeatMapLEgends";
import HeatMap from "./HeatMap";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import { getEndOfDay, getEndOfDayTimestamp, getStartOfDayTimestamp, timeStamptoDate } from "../../helpers/formatDate";
import { CommonMyDatePicker } from "../../components/Common/Datepicker/CommonMyDatePicker";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

// Map recenter component
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center.length === 2) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

export default function AnalysisReport() {
  const dispatch = useDispatch();
  const reportRef = useRef(null);
  const { divisions, ranges, rounds } = useSelector(
    (state) => state.Common || {}
  );

  // State for tickets and filtering
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [groupedTickets, setGroupedTickets] = useState([]);
  const [orientation, setOrientation] = useState("l");
  const [formData, setFormData] = useState({
    division: "",
    range: "",
    round: "",
    beat: "",
    assignedUser: "",
    status:"",
    startDate: "",
    endDate: "",
  });
  const [uniqueIncidentTypes, setUniqueIncidentTypes] = useState([]);

  // Keep track of current filters for display in PDF
  const [activeFilters, setActiveFilters] = useState({ status:"Resolved/Closed",
      incidentType: '2',
    });
  const [mapVisualization, setMapVisualization] = useState("none");
  // State for map data
  const [mapData, setMapData] = useState({
    type: "divisions", // 'divisions', 'ranges', 'rounds'
    coordinates: [],
    center: [19.9615, 79.2961],
    zoom: 17,
  });

  const [tooltipOpen, setTooltipOpen] = useState({
    email: false,
    whatsapp: false,
    download: false,
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [searchIncidentType, setSearchIncidentType] = useState("");
  const [activeTab, setActiveTab] = useState("heatMap");

  // Dropdown options
  const [filterOptions, setFilterOptions] = useState({
    divisions: [],
    ranges: [],
    rounds: [],
    beats: [],
    assignedUsers: [],
    statuses: [
      "resolved",
      "closed",
    ],
  });

  const getIncidentIcon = (incidentTypeId, incidentTypeName = "",typeofIncident) => {
    const matchedIncident = uniqueIncidentTypes.find(
      (incident) => incident.incidentTypeId === incidentTypeId
    );
    const type = matchedIncident?.incidentName || incidentTypeName;

    // Try to match exact key from INCIDENT_CONFIG first
    const configKey = Object.keys(INCIDENT_CONFIG).find((key) =>
      type.toLowerCase().includes(key.toLowerCase())
    );

    let color =""
    if(typeofIncident && typeofIncident.toLowerCase() == "death") {
      color="#FF6B6B"
    }else if(typeofIncident && typeofIncident.toLowerCase() == "injury") {
      color="#4ECDC4"
    }else{
      color = INCIDENT_CONFIG[configKey]?.color || INCIDENT_CONFIG.Other.color;

    }


    const createCustomIcon = (color) => {
      return new L.DivIcon({
        html: `<div style="background-color: ${color}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 2px 0 4px ${color};"></div>`,
        className: "custom-div-icon",
        iconSize: [20, 20],
        iconAnchor: [8, 8],
        popupAnchor: [0, -8],
      });
    };

    return createCustomIcon(color);
  };

  const dependentDropdowns = [
    {
      label: "Division",
      key: "division",
      data: divisions,
      fetch: fetchRanges2,
    },
    {
      label: "Range",
      key: "range",
      data: ranges,
      fetch: fetchRounds2,
    },
    {
      label: "Round",
      key: "round",
      data: rounds,
    },
  ];

  // Set default date range (last 30 days)
  useEffect(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    setFormData((prev) => ({
      ...prev,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    }));
    fetchReports(startDate.getTime(),endDate.getTime());


  }, []);

  // Convert coordinates array to leaflet format if needed
  const formatCoordinates = (coords) => {
    if (!coords || !Array.isArray(coords)) return [];
    return coords.map((coord) => [coord[1], coord[0]]); // Leaflet uses [lat, lng]
  };

  // Calculate center point of polygon
  const calculateCenter = (coords) => {
    if (!coords || !Array.isArray(coords) || coords.length === 0)
      return [19.9615, 79.2961];

    const latSum = coords.reduce((sum, point) => sum + point[0], 0);
    const lngSum = coords.reduce((sum, point) => sum + point[1], 0);

    return [latSum / coords.length, lngSum / coords.length];
  };
 const handleDateChange = (key, value) => {
  let datevalue=timeStamptoDate(value)
  if (key === "endDate") {
    datevalue =getEndOfDay(value);
  }
    let updatedFormData = { ...formData, [key]: datevalue };
    setFormData(updatedFormData);

 }
  // Modify handleInputChange to remove map data updates
  const handleInputChange = (e, key, fetchAction) => {
    const value = e.target.value;

    // Reset dependent dropdowns
    let updatedFormData = { ...formData, [key]: value };
    if (key === "division") {
      updatedFormData = {
        ...updatedFormData,
        range: "",
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch ranges
      if (value && value !== "0") {
        // Fetch ranges for this division
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "range") {
      updatedFormData = {
        ...updatedFormData,
        round: "",
        beat: "",
      };

      // If All is selected (value is '' or '0'), don't fetch rounds
      if (value && value !== "0") {
        // Fetch rounds for this range
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "round") {
      updatedFormData = {
        ...updatedFormData,
        beat: "",
      };
    }

    // Update form data
    setFormData(updatedFormData);
  };

  const toggleTooltip = (tooltipId) => {
    setTooltipOpen({
      ...tooltipOpen,
      [tooltipId]: !tooltipOpen[tooltipId],
    });
  };

  const fetchReports = async (startDate,endDate) => {
    const payload = {
      act: "dashboard",
      subact: "reports",
      info: {},
      filter:{
        fromDate: getStartOfDayTimestamp(startDate),
             toDate: getEndOfDayTimestamp(endDate),
      }
    };
    try {
      const response = await dashboard(payload);
      if (response?.message) {
        const sampleTickets = response?.data?.filter((ticket)=>ticket?.status=="Closed"|| ticket?.status=="Resolved") || [];
        const incidents = JSON.parse(
          localStorage.getItem("incidentTypes") || "[]"
        );
        setUniqueIncidentTypes(incidents);

        // Filter by default date range
        let dateFilteredTickets = filterTicketsByDateRange(
          sampleTickets,
          formData.startDate,
          formData.endDate
        );

        if (!searchIncidentType) {
          setSearchIncidentType(incidents[0].incidentTypeId);
          dateFilteredTickets = dateFilteredTickets.filter(
            (t) => t.incidentTypeId == incidents[0].incidentTypeId && (t.status =="Resolved" ||  t.status =="Closed")
          );
        }
        setTickets(dateFilteredTickets);

        // setActiveFilters({
        //   ...formData,
        //   status:"Resolved/Closed",
        //   incidentType: incidents[0].incidentTypeId,
        // });
        setFilteredTickets(dateFilteredTickets);

        // Group tickets initially
        groupTickets(dateFilteredTickets, formData);

        // Extract unique filter options
        const divisions = [
          ...new Set(sampleTickets.map((t) => t.divisionName)),
        ];
        const ranges = [...new Set(sampleTickets.map((t) => t.rangeName))];
        const rounds = [...new Set(sampleTickets.map((t) => t.roundName))];
        const beats = [...new Set(sampleTickets.map((t) => t.beatName))];
        setFilterOptions({
          divisions,
          ranges,
          rounds,
          beats,
          statuses: [
            "Resolved",
            "Closed",
          ],
        });
      }
    } catch (error) {
      toast.error("Error in fetching reports", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Generate PDF from the report with improved layout
  const generatePDF = async () => {
    if (!reportRef.current) return;
    showLoader();

    setIsGeneratingPDF(true);
    try {
      // Add a slight delay to ensure UI is settled
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Define document settings
      const headerHeight = 20;
      const footerHeight = 10;
      const pagePadding = 10;
      // Fix the orientation variable and provide a default value
      const pdf = new jsPDF("l", "mm", "a4", true);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Create filter string for display
      const filterText = [];
      if (activeFilters.division && activeFilters.division !== "0") {
        const divName =
          divisions?.data?.find(
            (div) => div.divisionId == activeFilters.division
          )?.divisionName || activeFilters.division;
        filterText.push(`Division: ${divName}`);
      }
      if (activeFilters.range && activeFilters.range !== "0") {
        const rangeName =
          ranges?.data?.find((r) => r.rangeId == activeFilters.range)?.name ||
          activeFilters.range;
        filterText.push(`Range: ${rangeName}`);
      }
      if (activeFilters.round && activeFilters.round !== "0") {
        const roundName =
          rounds?.data?.find((r) => r.roundId == activeFilters.round)?.name ||
          activeFilters.roundName;
        filterText.push(`Round: ${roundName}`);
      }
      if (activeFilters.status && activeFilters.status !== "0") {
        filterText.push(`Status: ${activeFilters.status}`);
      }
      if (activeFilters.incidentType) {
        const typeName =
          uniqueIncidentTypes.find(
            (i) => i.incidentTypeId == activeFilters.incidentType
          )?.incidentName || activeFilters.incidentType;
        filterText.push(`Incident Type: ${typeName}`);
      }

      // Date range is always present
      const dateFormat = { year: "numeric", month: "short", day: "numeric" };
      const startDateFormatted = new Date(
        formData.startDate
      ).toLocaleDateString("en-US", dateFormat);
      const endDateFormatted = new Date(formData.endDate).toLocaleDateString(
        "en-US",
        dateFormat
      );
      filterText.push(
        `Date Range: ${startDateFormatted} to ${endDateFormatted}`
      );

      // Current date for report
      const reportDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      // Enhanced helper function for safe image conversion with progressive fallbacks
      const safeCanvasToImage = async (
        element,
        elementDescription = "unknown",
        logDetails = false
      ) => {
        const logElementDetails = () => {
          if (logDetails) {
            console.log(`Element Details for ${elementDescription}:`, {
              element: element,
              width: element.offsetWidth,
              height: element.offsetHeight,
              pixelRatio: window.devicePixelRatio,
            });
          }
        };

        // Function to check if data URL is valid and usable
        const isValidDataUrl = (dataUrl) => {
          return (
            dataUrl &&
            typeof dataUrl === "string" &&
            dataUrl.startsWith("data:image/") &&
            dataUrl.length > 1000
          );
        };

        // First, try direct element to canvas with html2canvas
        const tryHtml2Canvas = async () => {
          logElementDetails();

          try {
            // Try to capture with standard html2canvas
            const canvas = await html2canvas(element, {
              scale: 2,
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "#ffffff",
              ignoreElements: (el) =>
                el.classList?.contains("loading-indicator") ||
                (el.classList &&
                  el.classList.contains("leaflet-control-attribution")),
            });

            const dataUrl = canvas.toDataURL("image/png");

            if (isValidDataUrl(dataUrl)) {
              return dataUrl;
            }
          } catch (err) {
            console.warn(`Html2Canvas failed for ${elementDescription}:`, err);
          }

          return null;
        };

        // Try direct canvas extraction if element is or contains a canvas
        const tryCanvasExtraction = async () => {
          // Check if element itself is a canvas
          if (element instanceof HTMLCanvasElement) {
            try {
              const dataUrl = element.toDataURL("image/png");
              if (isValidDataUrl(dataUrl)) return dataUrl;
            } catch (err) {
              console.warn(
                `Direct canvas conversion failed for ${elementDescription}:`,
                err
              );
            }
          }

          // Look for canvas elements inside
          const canvasElements = element.querySelectorAll("canvas");
          if (canvasElements.length > 0) {
            for (const canvas of canvasElements) {
              try {
                const dataUrl = canvas.toDataURL("image/png");
                if (isValidDataUrl(dataUrl)) return dataUrl;
              } catch (err) {
                console.warn(
                  `Canvas extraction failed for a canvas within ${elementDescription}:`,
                  err
                );
              }
            }
          }

          return null;
        };

        // Try cloned approach with html2canvas
        const tryClonedHtml2Canvas = async () => {
          // Clone the element to avoid modifying the original
          const clone = element.cloneNode(true);
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "absolute";
          tempDiv.style.left = "-9999px";
          tempDiv.style.top = "-9999px";
          tempDiv.appendChild(clone);
          document.body.appendChild(tempDiv);

          try {
            // Wait for clone to render
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Try to capture with html2canvas
            const canvas = await html2canvas(clone, {
              scale: 2,
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "#ffffff",
              ignoreElements: (el) =>
                el.classList?.contains("loading-indicator") ||
                (el.classList &&
                  el.classList.contains("leaflet-control-attribution")),
            });

            const dataUrl = canvas.toDataURL("image/png");

            if (isValidDataUrl(dataUrl)) {
              document.body.removeChild(tempDiv);
              return dataUrl;
            }
          } catch (err) {
            console.warn(
              `Cloned html2canvas failed for ${elementDescription}:`,
              err
            );
          } finally {
            // Clean up
            if (document.body.contains(tempDiv)) {
              document.body.removeChild(tempDiv);
            }
          }

          return null;
        };

        // Try each method in sequence with proper error handling
        const conversionMethods = [
          { name: "Html2Canvas", fn: tryHtml2Canvas },
          { name: "Canvas Extraction", fn: tryCanvasExtraction },
          { name: "Cloned Html2Canvas", fn: tryClonedHtml2Canvas },
        ];

        for (const method of conversionMethods) {
          try {
            console.log(`Trying ${method.name} for ${elementDescription}...`);
            const result = await method.fn();
            if (result) {
              console.log(`${method.name} succeeded for ${elementDescription}`);
              return result;
            }
          } catch (error) {
            console.error(`Error in ${method.name}:`, error);
          }
        }

        throw new Error(
          `All image conversion methods failed for ${elementDescription}`
        );
      };

      // Function to add consistent header to each page
      const addPageHeader = (pageNum) => {
        // Light background color for header
        pdf.setFillColor(240, 240, 240);
        pdf.rect(0, 0, pdfWidth, headerHeight, "F");

        // Title with black bold text
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Control Room Reports - ${reportDate}`, pdfWidth / 2, 8, {
          align: "center",
        });

        // Applied filters
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        if (filterText.length > 0) {
          const filtersString = filterText.join(" | ");
          const splitFilters = pdf.splitTextToSize(
            filtersString,
            pdfWidth - 20
          );
          pdf.text(splitFilters[0], pdfWidth / 2, 14, { align: "center" });
          if (splitFilters.length > 1) {
            pdf.text(splitFilters[1], pdfWidth / 2, 18, { align: "center" });
          }
        }

        // Footer with page number
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${pageNum}`, pdfWidth / 2, pdfHeight - 8, {
          align: "center",
        });
      };

      // Get map element and legend element
      const mapContainer =
        reportRef.current.querySelector(".leaflet-container");
      const legendContainer = mapContainer.parentElement.nextElementSibling;
      if (!mapContainer) {
        throw new Error("Map element not found");
      }
      // IMPORTANT: Store original map properties and temporarily set it to 700px fixed height
      const originalMapHeight = mapContainer.style.height;
      const originalMapWidth = mapContainer.style.width;
      const originalPosition = mapContainer.style.position;

      // Set fixed size and force refresh if needed
      mapContainer.style.height = "2000px";
      mapContainer.style.width = "90%";
      mapContainer.style.position = "relative";

      // Force a redraw of the map if necessary
      const map = mapContainer.__leaflet_instance__;
      if (map && typeof map.invalidateSize === "function") {
        map.invalidateSize(false);
      }

      // Add a delay to allow the map to fully render at new size
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Explicitly use html2canvas for the map, which works in the second example
      const mapCanvas = await html2canvas(mapContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
        ignoreElements: (element) => {
          // Ignore any loading indicators or unnecessary elements
          return (
            element.classList &&
            (element.classList.contains("loading-indicator") ||
              element.classList.contains("leaflet-control-attribution"))
          );
        },
      });

      // Capture the legend separately
      const legendCanvas = await html2canvas(legendContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });
      // Create a combined canvas
      const combinedCanvas = document.createElement("canvas");
      const ctx = combinedCanvas.getContext("2d");

      // Set combined canvas dimensions
      combinedCanvas.width = Math.max(mapCanvas.width, legendCanvas.width);
      combinedCanvas.height = mapCanvas.height + legendCanvas.height + 10; // 10px spacing

      // Fill with white background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

      // Draw map centered if needed
      const mapX =
        mapCanvas.width < combinedCanvas.width
          ? (combinedCanvas.width - mapCanvas.width) / 2
          : 0;
      ctx.drawImage(mapCanvas, mapX, 0);

      // Draw legend below map with styling
      ctx.fillStyle = "#f9f9f9";
      const legendY = mapCanvas.height + 10;
      const legendX =
        legendCanvas.width < combinedCanvas.width
          ? (combinedCanvas.width - legendCanvas.width) / 2
          : 0;

      // Draw legend background
      ctx.fillRect(legendX, legendY, legendCanvas.width, legendCanvas.height);
      ctx.strokeStyle = "#eeeeee";
      ctx.lineWidth = 1;
      legendCanvas.height>0 && ctx.strokeRect(legendX, legendY, legendCanvas.width, legendCanvas.height);

      // Draw legend content
      legendCanvas.height>0 &&  ctx.drawImage(legendCanvas, legendX, legendY);

      // Restore original map properties immediately
      mapContainer.style.height = originalMapHeight;
      mapContainer.style.width = originalMapWidth;
      mapContainer.style.position = originalPosition;

      // Force map to refresh to its original size
      if (map && typeof map.invalidateSize === "function") {
        map.invalidateSize(true);
      }

      // Give the map time to refresh before continuing
      await new Promise((resolve) => setTimeout(resolve, 100));

      // PAGE 1: Map with legend page
      addPageHeader(1);

      // Add combined map and legend to first page - calculate dimensions to fill the page properly
      const availableMapHeight =
        pdfHeight - headerHeight - footerHeight - pagePadding * 2;
      const mapAspectRatio = combinedCanvas.width / combinedCanvas.height;
      let mapWidth = pdfWidth - pagePadding * 2;
      let mapHeight = mapWidth / mapAspectRatio;

      // If map height exceeds available space, scale it down
      const combinedMapDataUrl = combinedCanvas.toDataURL("image/png");

      if (mapHeight > availableMapHeight) {
        mapHeight = availableMapHeight;
        mapWidth = mapHeight * mapAspectRatio * (orientation === "l" ? 1.2 : 1);

        // Center the map horizontally if it's not full width
        const leftMargin = (pdfWidth - mapWidth) / 2;
        pdf.addImage(
          combinedMapDataUrl,
          "PNG",
          leftMargin,
          headerHeight + pagePadding,
          mapWidth,
          mapHeight
        );
      } else {
        // Map fits with full width
        pdf.addImage(
          combinedMapDataUrl,
          "PNG",
          pagePadding,
          headerHeight + pagePadding,
          mapWidth,
          mapHeight
        );
      }

      // Now capture the incident analysis separately
      const incidentAnalysisElement =
        reportRef.current.querySelector(".incident-analysis");

      if (incidentAnalysisElement) {
        // Add a new page for content
        pdf.addPage();
        addPageHeader(2);

        // Allow time for any dynamic content to settle
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Use the same html2canvas approach that worked for the map
        const contentCanvas = await html2canvas(incidentAnalysisElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });

        // Calculate content dimensions for proper scaling
        const contentWidth = contentCanvas.width;
        const contentHeight = contentCanvas.height;
        const availableContentHeight =
          pdfHeight - headerHeight - footerHeight - pagePadding * 2.5;

        // Calculate scale to fit width
        const contentScale = Math.min(
          (pdfWidth - pagePadding * 2) / contentWidth,
          1
        );
        const scaledContentWidth = contentWidth * contentScale;
        const scaledContentHeight = contentHeight * contentScale * 0.9;

        // Determine if we need multiple pages
        const pagesNeeded = Math.ceil(
          scaledContentHeight / availableContentHeight
        );

        // If content fits on one page
        // if (pagesNeeded === 1) {
          pdf.addImage(
            contentCanvas.toDataURL("image/png"),
            "PNG",
            pagePadding,
            headerHeight + pagePadding,
            scaledContentWidth,
            scaledContentHeight
          );
        // } 
      }

      // Capture and add additional analysis charts
      const analysisCharts =
        reportRef.current.querySelectorAll(".analysyschart");

      if (analysisCharts && analysisCharts.length > 0) {
        // Process charts with improved stability
        for (let i = 0; i < analysisCharts.length; i++) {
          const chartElement = analysisCharts[i];

          // Add a new page for each chart
          pdf.addPage();
          addPageHeader(pdf.internal.getNumberOfPages());

          // Wait for any dynamic content to render
          await new Promise((resolve) => setTimeout(resolve, 150));

          try {
            // Get chart data using the same html2canvas approach that worked for the map
            const chartCanvas = await html2canvas(chartElement, {
              scale: 2,
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "#ffffff",
            });

            // Calculate chart dimensions for proper scaling
            const chartWidth = chartCanvas.width;
            const chartHeight = chartCanvas.height;
            const availableChartHeight =
              pdfHeight - headerHeight - footerHeight - pagePadding * 2.5;

            // Calculate scale to fit width
            const chartScale = Math.min(
              (pdfWidth - pagePadding * 2) / chartWidth,
              1
            );
            const scaledChartWidth = chartWidth * chartScale;
            const scaledChartHeight = chartHeight * chartScale * 0.95;

            // Add the chart to the PDF
            pdf.addImage(
              chartCanvas.toDataURL("image/png"),
              "PNG",
              pagePadding,
              headerHeight + pagePadding,
              scaledChartWidth,
              scaledChartHeight
            );
          } catch (chartError) {
            console.error(`Failed to add chart ${i} to PDF:`, chartError);
            // If standard approach fails, try using our fallback
            try {
              // Try the fallback method
              const chartDataUrl = await safeCanvasToImage(
                chartElement,
                `Analysis Chart ${i}`,
                true
              );

              // Create a temporary image element to get dimensions
              const chartImg = new Image();
              await new Promise((resolve) => {
                chartImg.onload = resolve;
                chartImg.src = chartDataUrl;
              });

              // Calculate chart dimensions for proper scaling
              const chartWidth = chartImg.width;
              const chartHeight = chartImg.height;
              const availableChartHeight =
                pdfHeight - headerHeight - footerHeight - pagePadding * 2.5;

              // Calculate scale to fit width
              const chartScale = Math.min(
                (pdfWidth - pagePadding * 2) / chartWidth,
                1
              );
              const scaledChartWidth = chartWidth * chartScale;
              const scaledChartHeight = chartHeight * chartScale * 0.95;

              // Add the chart to the PDF
              pdf.addImage(
                chartDataUrl,
                "PNG",
                pagePadding,
                headerHeight + pagePadding,
                scaledChartWidth,
                scaledChartHeight
              );
            } catch (fallbackError) {
              console.error(
                `Fallback method also failed for chart ${i}:`,
                fallbackError
              );
            }
          }
        }
      }

      pdf.save("control-room-report.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error generating PDF report", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      hideLoader();
      setIsGeneratingPDF(false);
    }
  };

  // Filter tickets by date range
  const filterTicketsByDateRange = (tickets, startDate, endDate) => {
    if (!startDate || !endDate) return tickets;

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return tickets.filter((ticket) => {
      const ticketDate = new Date(ticket.createdAt);
      return ticketDate >= start && ticketDate <= end;
    });
  };
 
  // Group tickets based on selection
  const groupTickets = (filteredTickets, currentFormData) => {
    let groupedData = [];

    // Helper function to calculate status counts
    const getStatusCounts = (tickets) => {
      return {
        open: tickets.filter((t) => t.status.toLowerCase() === "open").length,
        inProgress: tickets.filter((t) =>
          ["working", "assigned", "acknowledged"].includes(
            t.status.toLowerCase()
          )
        ).length,
        resolved: tickets.filter((t) =>
          ["resolved"].includes(t.status.toLowerCase())
        ).length,
        closed: tickets.filter((t) =>
          ["closed"].includes(t.status.toLowerCase())
        ).length,
      };
    };
    const getCategoryCounts = (
      tickets = [],
      groupingOptions = GROUPING_OPTIONS
    ) => {
      const result = {};
    
      groupingOptions.forEach(({ key, nameKey }) => {
        // Check if the key exists in any ticket
        const hasKey = tickets.some(
          (ticket) => ticket[key] !== undefined && ticket[key] !== null
        );
    
        if (!hasKey) return;
    
        const categoryCounts = {};
    
        tickets.forEach((ticket) => {
          let categoryValue = ticket[key];
    
          if (key === 'age') {
            const age = parseInt(categoryValue, 10);
    
            if (!isNaN(age)) {
              if (age >= 1 && age <= 20) {
                categoryValue = '1-20';  // Using hyphen instead of "to"
              } else if (age >= 21 && age <= 40) {
                categoryValue = '21-40'; // Using hyphen instead of "to"
              } else if (age >= 41 && age <= 60) {
                categoryValue = '41-60'; // Using hyphen instead of "to"
              } else if (age >= 61) {
                categoryValue = 'Above 61';
              } else {
                return; // Skip if age is invalid (e.g. 0 or negative)
              }
            } else {
              return; // Skip if age is not a number
            }
          }
    
          if (categoryValue) {
            if (!categoryCounts[categoryValue]) {
              categoryCounts[categoryValue] = {
                count: 0,
                name: categoryValue,
              };
            }
    
            categoryCounts[categoryValue].count++;
          }
        });
    
        // For age data specifically, convert to array, sort, then convert back to object
        if (key === 'age') {
          // Define the order of age categories
          const ageOrder = {
            '1-20': 1,
            '21-40': 2,
            '41-60': 3,
            'Above 61': 4
          };
          
          // Convert to array for sorting
          let categoryArray = Object.values(categoryCounts);
          
          // Sort the categories based on the predefined order
          categoryArray.sort((a, b) => {
            return ageOrder[a.name] - ageOrder[b.name];
          });
          
          // Convert back to object with sorted entries
          const sortedCategoryCounts = {};
          categoryArray.forEach(item => {
            sortedCategoryCounts[item.name] = item;
          });
          
          result[nameKey] = sortedCategoryCounts;
        } else {
          // For non-age categories, use as is
          result[nameKey] = categoryCounts;
        }
      });
    
      return result;
    };
    
    // Helper function to calculate incident type counts
    const getIncidentTypeCounts = (tickets) => {
      const incidentCounts = {};

      tickets.forEach((ticket) => {
        const incidentTypeId = ticket.incidentTypeId || "unknown";
        if (!incidentCounts[incidentTypeId]) {
          // Find the incident type name from the uniqueIncidentTypes array
          const incidentName =
            uniqueIncidentTypes.find(
              (incident) => incident.incidentTypeId == incidentTypeId
            )?.incidentName || "Unknown";

          incidentCounts[incidentTypeId] = {
            count: 0,
            name: incidentName,
          };
        }
        incidentCounts[incidentTypeId].count++;
      });

      return incidentCounts;
    };

    // If division is selected (including when division is selected and range is "All")
    if (currentFormData.division && currentFormData.division !== "0") {
      // If range is also selected but not round (or round is "All")
      if (currentFormData.range && currentFormData.range !== "0") {
        // Group by round
        const groupByRound = filteredTickets.reduce((acc, ticket) => {
          const key = ticket.roundName || "Unknown Round";
          if (!acc[key]) {
            acc[key] = {
              tickets: [],
              name: key,
              group: "Round",
              totalBurnedArea: 0,
            };
          }
          acc[key].tickets.push(ticket);
          if (ticket.burnedArea !== undefined && ticket.burnedArea !== null) {
            const burnedArea = parseFloat(ticket.burnedArea);
            if (!isNaN(burnedArea)) {
              acc[key].totalBurnedArea += burnedArea;
            }
          }
          return acc;
        }, {});

        // Convert grouped object to array for display with status counts
        groupedData = Object.keys(groupByRound).map((roundName) => {
          const roundTickets = groupByRound[roundName].tickets;
          const statusCounts = getStatusCounts(roundTickets);
          const incidentTypeCounts = getIncidentTypeCounts(roundTickets);
          const catergotyCounts = getCategoryCounts(roundTickets);
          return {
            name: roundName,
            catergotyCounts,
            count: roundTickets.length,
            group: "Round",
            openCount: statusCounts.open,
            inProgressCount: statusCounts.inProgress,
            closedCount:statusCounts.closed,
            resolvedCount: statusCounts.resolved,
            incidentTypeCounts: incidentTypeCounts,
            totalBurnedArea: groupByRound[roundName].totalBurnedArea || 0,
          };
        });
      } else {
        // Group by range when division is selected but range is not (or range is "All")
        const groupByRange = filteredTickets.reduce((acc, ticket) => {
          const key = ticket.rangeId;
          if (!acc[key]) {
            acc[key] = {
              tickets: [],
              id: key,
              name: ticket.rangeName || "Unknown Range",
              group: "Range",
              totalBurnedArea: 0,
            };
          }
          acc[key].tickets.push(ticket);
          if (ticket.burnedArea !== undefined && ticket.burnedArea !== null) {
            const burnedArea = parseFloat(ticket.burnedArea);
            if (!isNaN(burnedArea)) {
              acc[key].totalBurnedArea += burnedArea;
            }
          }
          return acc;
        }, {});

        // Convert grouped object to array for display with status counts
        groupedData = Object.keys(groupByRange).map((rangeId) => {
          const rangeTickets = groupByRange[rangeId].tickets;
          const rangeName =
            dependentDropdowns
              .filter((item) => item.key == "range")[0]
              ?.data?.data?.filter((item) => item?.rangeId == rangeId)[0]
              ?.rangeName || groupByRange[rangeId].name;
          const statusCounts = getStatusCounts(rangeTickets);
          const incidentTypeCounts = getIncidentTypeCounts(rangeTickets);
          const catergotyCounts = getCategoryCounts(rangeTickets);

          return {
            rangeId,
            name: rangeName,
            count: rangeTickets.length,
            catergotyCounts,
            group: "Range",
            openCount: statusCounts.open,
            inProgressCount: statusCounts.inProgress,
            resolvedCount: statusCounts.resolved,
            closedCount:statusCounts.closed,

            incidentTypeCounts: incidentTypeCounts,
            totalBurnedArea: groupByRange[rangeId].totalBurnedArea || 0,
          };
        });
      }
    }
    // If no division is selected, group by division
    else {
      // Group by division
      const groupByDivision = filteredTickets.reduce((acc, ticket) => {
        const key = ticket.divisionId;
        if (!acc[key]) {
          acc[key] = {
            tickets: [],
            key: key,
            name: ticket.divisionName || "Unknown Division",
            group: "Division",
            totalBurnedArea: 0,
          };
        }
        acc[key].tickets.push(ticket);
        if (ticket.burnedArea !== undefined && ticket.burnedArea !== null) {
          const burnedArea = parseFloat(ticket.burnedArea);
          if (!isNaN(burnedArea)) {
            acc[key].totalBurnedArea += burnedArea;
          }
        }
        return acc;
      }, {});

      // Convert grouped object to array for display with status counts
      groupedData = Object.keys(groupByDivision).map((divisionName) => {
        const divisionTickets = groupByDivision[divisionName].tickets;
        const divisionNamea =
          dependentDropdowns
            .filter((item) => item.key == "division")[0]
            ?.data?.data?.filter((item) => item?.divisionId == divisionName)[0]
            ?.divisionName || groupByDivision[divisionName].name;
        const statusCounts = getStatusCounts(divisionTickets);
        const incidentTypeCounts = getIncidentTypeCounts(divisionTickets);
        const catergotyCounts = getCategoryCounts(divisionTickets);

        return {
          name: divisionNamea || "Unknown Division",
          count: divisionTickets.length,
          group: "Division",
          catergotyCounts,
          openCount: statusCounts.open,
          inProgressCount: statusCounts.inProgress,
          closedCount:statusCounts.closed,

          resolvedCount: statusCounts.resolved,
          incidentTypeCounts: incidentTypeCounts,
          totalBurnedArea: groupByDivision[divisionName].totalBurnedArea || 0,
        };
      });
    }

    setGroupedTickets(groupedData);
  };

  // Filter tickets based on all criteria
  const filterTickets =async (currentFormData = formData) => {
   await fetchReports(new Date(formData?.startDate).getTime(), new  Date(formData?.endDate).getTime());
    let filtered = [...tickets];


    // Apply date range filter
    filtered = filterTicketsByDateRange(
      filtered,
      currentFormData.startDate,
      currentFormData.endDate
    );

    // Apply division filter
    if (currentFormData.division && currentFormData.division !== "0") {
      filtered = filtered.filter(
        (t) => t.divisionId == currentFormData.division
      );
    }
    // Apply range filter
    if (currentFormData.range && currentFormData.range !== "0") {
      filtered = filtered.filter((t) => t.rangeId == currentFormData.range);
    }

    // Apply round filter
    if (currentFormData.round && currentFormData.round !== "0") {
      filtered = filtered.filter((t) => t.roundId == currentFormData.round);
    }

    // Apply status filter
    if (currentFormData.status && currentFormData.status !== "0") {
      filtered = filtered.filter(
        (t) => t.status.toLowerCase() === currentFormData.status.toLowerCase()
      );
    }

    // Apply incident type filter
    if (searchIncidentType) {
      filtered = filtered.filter((t) => t.incidentTypeId == searchIncidentType);
    }

    setFilteredTickets(filtered);

    // Also update the grouped tickets
    groupTickets(filtered, currentFormData);
  };

  // Load initial dropdown data and set up map
  useEffect(() => {
    dispatch(fetchDivisions());

    // Only try to load initial data if we have form values on first render
    const { division, range, round, beat } = formData;
    if (division) {
      dispatch(fetchRanges(division));

      if (range) {
        dispatch(fetchRounds(range));

        if (round) {
          dispatch(fetchBeats(round));
        }
      }
    }

  }, []);

  // Update map data when divisions are loaded
  useEffect(() => {
    if (divisions?.data?.length > 0) {
      const allCoordinates = divisions.data
        .filter((div) => div.coordinates && Array.isArray(div.coordinates))
        .map((div) => formatCoordinates(div.coordinates));

      setMapData({
        type: "divisions",
        coordinates: allCoordinates,
        center: [19.9615, 79.2961],
        zoom: 10,
      });
    }
  }, [divisions]);

  // Apply filters when button is clicked
  const applyFilters = () => {
    // Save the current filters for PDF display
    setActiveFilters({
      ...formData,
      incidentType: searchIncidentType,
    });

    // Apply filters
    filterTickets();

    // Update map data based on current filter selections
    updateMapData();
  };

  const updateMapData = () => {
    // Default map view (all divisions)
    let newMapData = {
      type: "divisions",
      coordinates:
        divisions?.data?.map((div) => formatCoordinates(div.coordinates)) || [],
      center: [20.5937, 78.9629],
      zoom: 10,
    };

    // If division is selected
    if (formData.division && formData.division !== "0") {
      const selectedDivision = divisions?.data?.find(
        (div) => div.divisionId == formData.division
      );
      if (selectedDivision && selectedDivision.coordinates) {
        const formattedCoords = formatCoordinates(selectedDivision.coordinates);
        const center = calculateCenter(formattedCoords);
        newMapData = {
          type: "division",
          coordinates: [formattedCoords],
          center: center,
          zoom: 11,
        };

        // If range is selected
        if (formData.range && formData.range !== "0") {
          const selectedRange = ranges?.data?.find(
            (range) => range.rangeId == formData.range
          );
          if (selectedRange && selectedRange.coordinates) {
            const formattedCoords = formatCoordinates(
              selectedRange.coordinates
            );
            const center = calculateCenter(formattedCoords);
            newMapData = {
              type: "range",
              coordinates: [formattedCoords],
              center: center,
              zoom: 12,
            };

            // If round is selected
            if (formData.round && formData.round !== "0") {
              const selectedRound = rounds?.data?.find(
                (round) => round.roundId == formData.round
              );
              if (selectedRound && selectedRound.coordinates) {
                const formattedCoords = formatCoordinates(
                  selectedRound.coordinates
                );
                const center = calculateCenter(formattedCoords);
                newMapData = {
                  type: "round",
                  coordinates: [formattedCoords],
                  center: center,
                  zoom: 12,
                };
              }
            }
          }
        }
      }
    }

    // Update map data
    setMapData(newMapData);
  };

  const resetFilters = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    const resetFormData = {
      division: "",
      range: "",
      round: "",
      beat: "",
      assignedUser: "",
      status: "",
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };

    // Clear active filters
    setActiveFilters({
      ...formData,
      status:"Resolved/Closed",
      incidentType: uniqueIncidentTypes[0].incidentTypeId,
    });

    // Reset form data
    setFormData(resetFormData);
    setSearchIncidentType(uniqueIncidentTypes[0].incidentTypeId);

    // Reset map view to show all divisions
    setMapData({
      type: "divisions",
      coordinates:
        divisions?.data?.map((div) => formatCoordinates(div.coordinates)) || [],
      center: [20.5937, 78.9629],
      zoom: 10,
    });

    // Reset to filtered tickets by date only
    let dateFilteredTickets = filterTicketsByDateRange(
      tickets,
      resetFormData.startDate,
      resetFormData.endDate
    );
      // Apply incident type filter
      if (searchIncidentType) {
        dateFilteredTickets = dateFilteredTickets.filter((t) => t.incidentTypeId == searchIncidentType);
      }

    setFilteredTickets(dateFilteredTickets);

    // Reset grouped tickets
    groupTickets(dateFilteredTickets, resetFormData);
  };

  const tabs = [
    { id: "heatMap", label: "Heat Map" },
    { id: "incidentMap", label: "Incidents Map" },
  ];

  const heatmapPoint = filteredTickets
  .filter(
    (ticket) =>
      ticket?.incidentLocation?.lat && ticket?.incidentLocation?.long
  )
  .map((ticket) => ({
    lat: ticket.incidentLocation.lat,
    lng: ticket.incidentLocation.long,
    intensity: 1, // you can adjust this dynamically per ticket
  }));
  console.log("formData",formData)

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Control Room" breadcrumbItem="Analysis" />

        {/* Filters Section */}
        <Card>
          <CardBody>
            <Row className="mb-3">
              <Col md={6}>
                <h4 className="card-title">Filters</h4>
              </Col>
            </Row>

            <Row>
              {/* Date range inputs */}
              <Col md={2}>
                <FormGroup>
                  <CommonDatePicker
                    label="Start Date"
                    value={formData.startDate}
                    maxDate={formData.endDate}
                    onChange={(timestamp) =>
                      handleDateChange("startDate", timestamp)
                    }
                    name={"Start Date"}
                    preserveTime={true}
                  />
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <CommonMyDatePicker
                    label="End Date"
                    value={formData.endDate}
                    minDate={formData.startDate}
                    onChange={(timestamp) =>
                      handleDateChange("endDate", getEndOfDay(timestamp))
                    }
                    name={"End Date"}
                    preserveTime={true}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label>Incident Type</Label>
                  <Input
                    type="select"
                    value={searchIncidentType}
                    onChange={(e) => setSearchIncidentType(e.target.value)}
                  >
                    {uniqueIncidentTypes.map((incident) => (
                      <option key={incident.id} value={incident.incidentTypeId}>
                        {incident.incidentName}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* Dependent Dropdowns */}
              {dependentDropdowns.map(({ label, key, data, fetch }, index) => (
                <Col md={2} key={key}>
                  <FormGroup>
                    <Label for={key}>{label}</Label>
                    <Input
                      type="select"
                      id={key}
                      value={formData[key] || ""}
                      onChange={(e) => handleInputChange(e, key, fetch)}
                      disabled={
                        (index > 0 &&
                          !formData[
                            ["division", "range", "round"][index - 1]
                          ]) ||
                        (index > 0 && formData["division"] === "0")
                      }
                    >
                      <option value="0">All</option>
                      {Array.isArray(data?.data) &&
                        data.data.map((item) => (
                          <option
                            key={item[`${key}Id`]}
                            value={item[`${key}Id`]}
                          >
                            {item.name ||
                              item.divisionName ||
                              item.rangeName ||
                              item.beatName ||
                              item.roundName}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
              ))}

              <Col md={2}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={formData.status || ""}
                    onChange={(e) => handleInputChange(e, "status", null)}
                  >
                    <option value="0">Resolved/ Closed</option>
                    {filterOptions?.statuses.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Button color="primary" onClick={applyFilters}>
                  Apply
                </Button>
                <Button
                  color="secondary"
                  className="ms-2"
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Col>
              <Col md={6} className="text-end">
                {/* <Label className="mr-4">
                  <Input
                    type="radio"
                    name="orientation"
                    value="p"
                    checked={orientation === "p"}
                    onChange={(e) => setOrientation(e.target.value)}
                  />{" "}
                  Portrait
                </Label>
                <Label className="mx-3">
                  <Input
                    type="radio"
                    name="orientation"
                    value="l"
                    checked={orientation === "l"}
                    onChange={(e) => setOrientation(e.target.value)}
                  />{" "}
                  Landscape
                </Label> */}
                <ButtonGroup>
                  <Button
                    color="primary"
                    id="downloadPdfBtn"
                    onClick={generatePDF}
                    disabled={isGeneratingPDF}
                  >
                    <Download size={16} className="me-1" />
                    {isGeneratingPDF ? "Generating..." : "Download PDF"}
                  </Button>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.download}
                    target="downloadPdfBtn"
                    toggle={() => toggleTooltip("download")}
                  >
                    Download report as PDF
                  </Tooltip>
                </ButtonGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Active Filters Display - This will be useful for users but won't be part of the PDF */}
        {Object.keys(activeFilters).some(
          (key) =>
            activeFilters[key] &&
            activeFilters[key] !== "0" &&
            key !== "startDate" &&
            key !== "endDate"
        ) && (
          <Card className="mb-3">
            <CardBody>
              <h5>Active Filters:</h5>
              <div className="d-flex flex-wrap gap-2">
                {activeFilters.division && activeFilters.division !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Division:{" "}
                    {divisions?.data?.find(
                      (div) => div.divisionId == activeFilters.division
                    )?.divisionName || activeFilters.division}
                  </div>
                )}
                {activeFilters.range && activeFilters.range !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Range:{" "}
                    {ranges?.data?.find((r) => r.rangeId == activeFilters.range)
                      ?.rangeName || activeFilters.range}
                  </div>
                )}

                {activeFilters.round && activeFilters.round !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Round:{" "}
                    {rounds?.data?.find((r) => r.roundId == activeFilters.round)
                      ?.roundName || activeFilters.round}
                  </div>
                )}

                {activeFilters.status && activeFilters.status !== "0" && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Status: {activeFilters.status}
                  </div>
                )}

                {activeFilters.incidentType && (
                  <div className="badge bg-light text-dark me-2 p-2">
                    Incident Type:{" "}
                    {uniqueIncidentTypes.find(
                      (i) => i.incidentTypeId == activeFilters.incidentType
                    )?.incidentName || activeFilters.incidentType}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Report content to be captured for PDF */}
        <div ref={reportRef}>
          <div className="d-none">
            <h1 className="text-center mb-4">Control Room Dashboard Report</h1>
            <p className="text-center mb-4">
              Generated on:{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          <div className="pdf-header d-none"></div>

          {/* Map Section */}
          <Card>
            <CardBody>
              <h4 className="card-title">Incident Locations</h4>
              <em style={{ color: "red" }}>
                Please Adjust the map before generating PDF.
              </em>
              <div className="d-flex gap-2">
                {tabs?.map((tab) => (
                  <>
                    <Nav
                      key={tab.id}
                      tabs
                      className="nav-tabs-custom nav-justified"
                    >
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            active: activeTab === tab.id,
                          })}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          <span className="d-none d-md-inline-block">
                            {" "}
                            {tab.label}
                          </span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </>
                ))}
              </div>

              <div>
                {activeTab == "heatMap" && (
                  <div
                    style={{ display: "flex", height: "2000px", width: "100%" }}
                  >
                    <MapContainer
                      center={mapData?.center}
                      zoom={mapData?.zoom}
                      preferCanvas={true}
                      renderer={L.canvas()}
                      style={{
                        height: "100%",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      <TileLayer
                        attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      {/* Fixed HeatmapLayer */}
                      {heatmapPoint && heatmapPoint.length > 0 && (
                        <HeatmapLayer
                          points={heatmapPoint}
                          longitudeExtractor={(m) => m.lng}
                          latitudeExtractor={(m) => m.lat}
                          intensityExtractor={(m) => parseFloat(m.lng)}
                          radius={10}
                          max={10}
                          minOpacity={1}
                          blur={10}
                          gradient={{
                            0.2: "blue",
                            0.5: "lime",
                            0.8: "yellow",
                            1.0: "red",
                          }}
                        />
                      )}
                      {/* <HeatmapLegend /> */}

                      <MapController
                        center={mapData.center}
                        zoom={mapData.zoom}
                      />
                      <CanvasOverlay
                        data={filteredTickets}
                        drawMethod={mapVisualization}
                      />

                      {mapData?.coordinates &&
                        mapData?.coordinates.map(
                          (coords, index) =>
                            coords &&
                            coords.length > 0 && (
                              <Polygon
                                key={index}
                                positions={coords}
                                pathOptions={{
                                  color:
                                    mapData.type == "division"
                                      ? "blue"
                                      : mapData.type === "range"
                                      ? "green"
                                      : mapData.type === "round"
                                      ? "purple"
                                      : "grey",
                                  fillColor:
                                    mapData.type == "division"
                                      ? "blue"
                                      : mapData.type === "range"
                                      ? "green"
                                      : mapData.type === "round"
                                      ? "purple"
                                      : "orange",
                                  fillOpacity: 0,
                                  weight: 2,
                                }}
                              />
                            )
                        )}
                    </MapContainer>
                  </div>

                  // <HeatMap
                  //   filteredTickets={filteredTickets}
                  //   mapData={mapData}
                  //   setMapData={setMapData}
                  // />
                )}
               {activeTab == "incidentMap" && (
  <>
    <div
      style={{
        display: "flex",
        height: "2000px",
        width: "100%",
      }}
    >
      <MapContainer
        center={mapData?.center}
        zoom={mapData?.zoom}
        preferCanvas={true}
        renderer={L.canvas()}
        style={{
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapController
          center={mapData.center}
          zoom={mapData.zoom}
        />

                        <CanvasOverlay
                          data={filteredTickets}
                          drawMethod={mapVisualization}
                        />
                        {mapData?.coordinates &&
                          mapData?.coordinates.map(
                            (coords, index) =>
                              coords &&
                              coords.length > 0 && (
                                <Polygon
                                  key={index}
                                  positions={coords}
                                  pathOptions={{
                                    color:
                                      mapData.type == "division"
                                        ? "blue"
                                        : mapData.type === "range"
                                        ? "green"
                                        : mapData.type === "round"
                                        ? "purple"
                                        : "grey",
                                    fillColor:
                                      mapData.type == "division"
                                        ? "blue"
                                        : mapData.type === "range"
                                        ? "green"
                                        : mapData.type === "round"
                                        ? "purple"
                                        : "orange",
                                    fillOpacity: 0,
                                    weight: 2,
                                  }}
                                />
                              )
                          )}
                        {filteredTickets.map(
                          (ticket, index) =>
                            ticket?.incidentLocation &&
                            ticket?.incidentLocation?.lat &&
                            ticket?.incidentLocation?.long && (
                              <Marker
                                key={`marker-${index}`}
                                position={[
                                  ticket?.incidentLocation?.lat,
                                  ticket?.incidentLocation?.long,
                                ]}
                                icon={getIncidentIcon(
                                  ticket.incidentTypeId,
                                  ticket.incidentTypeName,
                                  ticket?.typeofIncident
                                )}
                              >
                                <Popup>
                                  <div>
                                    <h6>Ticket: {ticket.serialId || "N/A"}</h6>
                                    <p>Status: {ticket.status || "N/A"}</p>
                                    <p>
                                      Division: {ticket.divisionName || "N/A"}
                                    </p>
                                    <p>Range: {ticket.rangeName || "N/A"}</p>
                                    <p>Round: {ticket.roundName || "N/A"}</p>
                                    {ticket.description && (
                                      <p>
                                        Description:{" "}
                                        {ticket.description.substring(0, 100)}
                                        ...
                                      </p>
                                    )}
                                  </div>
                                </Popup>
                              </Marker>
                            )
                        )}
                      </MapContainer>
                    </div>
                    <div className=" align-items-center mt-3">

                   
                    {/* <ul className="d-flex mt-2 flex-wrap list-unstyled">
                      {Object.entries(INCIDENT_CONFIG).map(
                        ([typeName, config]) => (
                          <li
                            key={typeName}
                            className="me-3 mb-2 d-flex align-items-center"
                          >
                            <div
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: config.color,
                                border: "2px solid white",
                                boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                                marginRight: 5,
                              }}
                            ></div>
                            <span>{typeName}</span>
                          </li>
                        )
                      )}
                    </ul> */}
                    {/* <div>  */}
                     {activeFilters.incidentType && (activeFilters.incidentType == "2"||activeFilters.incidentType=="3")&& 
                      <ul className="d-flex mt-2 flex-wrap list-unstyled">
                      {Object.entries(DEATH_INJURED_CONFIG).map(
                        ([typeName, config]) => (
                          <li
                            key={typeName}
                            className="me-3 mb-2 d-flex align-items-center"
                          >
                            <div
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: config.color,
                                border: "2px solid white",
                                boxShadow: "0 0 4px rgba(0,0,0,0.5)",
                                marginRight: 5,
                              }}
                            ></div>
                            <span>{typeName}</span>
                          </li>
                        )
                      )}
                    </ul>}
                    {/* </div> */}
                    </div>
                  </>
                )}


                <div></div>
              </div>
            </CardBody>
          </Card>

          {filteredTickets?.length > 0 && (
            <IncidentAnalysis
              filteredTickets={filteredTickets}
              groupedTickets={groupedTickets}
              uniqueIncidentTypes={uniqueIncidentTypes}
              searchIncidentType={searchIncidentType}
            />
          )}

          <div className="pdf-footer d-none"></div>
        </div>
      </Container>
    </div>
  );
}
